/**
 * The organisation segment for a workspace that has no organisation.
 *
 * A personal workspace still needs a segment, so that every workspace URL has
 * the same shape and `/:role/:org` never has to be optional. "me" rather than
 * the principal's own ONDO id: the id is already in the path's meaning, and a
 * URL a collector might read aloud should not carry their identifier twice.
 */
export const PERSONAL_SEGMENT = "me";

/** Every path that is not derived from a workspace. */
export const PATHS = {
  root: "/",
  signIn: "/signin",
  apply: "/apply",
  /** Ask for a reset link. Signed out, like sign-in and apply. */
  forgotPassword: "/forgot-password",
  /**
   * Where an emailed reset link lands, and the SECOND address outside both route gates.
   *
   * **There is no `:token` here, and there is no query parameter either.** Supabase puts
   * its recovery tokens in the URL FRAGMENT, which a browser never sends to a server —
   * so they appear in no access log and no `Referer` header. The client reads them
   * itself; nothing in ONDO's code sees them. The address is therefore the same for
   * everybody.
   *
   * It must also be listed in `additional_redirect_urls` in `supabase/config.toml`:
   * GoTrue silently falls back to `site_url` for an address it does not recognise, and
   * the result is a link that works and lands on the wrong page.
   */
  resetPassword: "/reset-password",
  workspaces: "/workspaces",
  /**
   * A viewing room. The one address that does not ask who is reading it.
   *
   * `/room/` singular, matching the API's `@Public()` prefix and unlike the
   * collector's own plural `viewing-rooms`, so a link a collector pastes into an
   * email is visibly not a page inside their workspace.
   */
  room: "/room/:handle/:slug",
  /**
   * Check a proof bundle. The second address that does not ask who is reading
   * it, and the one that reads nothing from ONDO: the page hashes a file the
   * visitor already holds and makes no request. Outside both gates for the
   * room's reason, and public for a reason of its own — a proof a reader could
   * only check by signing in to the party that issued it would be a proof of
   * nothing.
   */
  proof: "/proof",
} as const;

/**
 * The search parameters that open a screen ON something.
 *
 * Search parameters rather than path segments, because none of these names a
 * different screen — they name what to focus once it has loaded, and a URL
 * without one is the same page in its default state. Putting them in the path
 * would mean a route for every combination and a 404 for a stale id.
 *
 * They exist for the notifications inbox, whose whole job is to route out to
 * the place an act happens. Nothing stops them being used for an emailed link
 * later; nothing about them assumes a notification.
 */
export const FOCUS_PARAMS = {
  /** A verification request, on the Verifications screen. */
  request: "request",
  /** An affiliation claim, on the Verifications screen's second tab. */
  claim: "claim",
  /** A representation request, on the Verifications screen's third tab. */
  representation: "representation",
  /** A link in a chain of title, on a work's record. */
  entry: "entry",
  /**
   * Which tab of a work's record is open.
   *
   * Not a row to land on, unlike the three above — it is which reading of the
   * record is showing, and four of the six tabs are the same rows narrowed by
   * kind. It is here for the reason the others are: without it a reload, a
   * bookmark and a pasted link all drop back to Artwork history, and a reader
   * who found a condition report cannot send anyone to it.
   */
  tab: "tab",
  /**
   * The New collection form, open, on the Collections screen.
   *
   * The button that opens it lives in the shell's header, which is a frame
   * around the screen and holds none of its state — so the header links to
   * the screen WITH this parameter, and the screen reads it. The same rule as
   * the four above: a URL without it is the same page in its default state,
   * and a reload, a bookmark and Back all behave.
   */
  create: "new",
} as const;

export const ROUTE_COPY = {
  booting: "Loading…",
  notFoundTitle: "That page doesn’t exist",
  notFound:
    "The address may be mistyped, or it may point at something that has since moved.",
  notFoundAction: "Go to your workspaces",
} as const;
